import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { Play, Plus, Clock, Users, X, Star, CheckCircle, Award } from "lucide-react"
import { Card, CardContent } from "./ui/card"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Progress } from "./ui/progress"
import ModalDesmatricula from "./ModalDesmatricula"

export function CourseCard({ course, delay = 0, onMatricula, onVerDetalhes, loadingMatricula = false, onGerarCertificado, onVerCertificado }) {
  const cardRef = useRef(null)
  const [isHovered, setIsHovered] = useState(false)
  const [showModalDesmatricula, setShowModalDesmatricula] = useState(false)
  const [loadingCertificado, setLoadingCertificado] = useState(false)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animação inicial
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 40, scale: 0.9 },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.8,
          delay,
          ease: "power3.out",
        },
      )
    }, cardRef)

    return () => ctx.revert()
  }, [delay])

  const handleMouseEnter = () => {
    setIsHovered(true)
    gsap.to(cardRef.current, {
      y: -12,
      scale: 1.03,
      duration: 0.4,
      ease: "power3.out",
    })
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    gsap.to(cardRef.current, {
      y: 0,
      scale: 1,
      duration: 0.4,
      ease: "power3.out",
    })
  }

  const handleMatriculaClick = () => {
    if (course.matriculado) {
      // Se já está matriculado, verificar se o curso está concluído
      const cursoConcluido = course.progresso && course.progresso.porcentagem === 100;
      
      if (cursoConcluido) {
        // Se o curso está concluído, não mostrar o botão de desmatrícula
        return;
      } else {
        // Se não está concluído, mostrar modal de confirmação
        setShowModalDesmatricula(true);
      }
    } else {
      // Se não está matriculado, matricular normalmente
      onMatricula && onMatricula(course.id, course.matriculado);
    }
  }

  const handleConfirmarDesmatricula = () => {
    setShowModalDesmatricula(false);
    onMatricula && onMatricula(course.id, true);
  }

  const handleFecharModal = () => {
    setShowModalDesmatricula(false);
  }

  const handleGerarCertificado = async () => {
    if (!cursoConcluido) return;
    
    setLoadingCertificado(true);
    try {
      await onGerarCertificado(course.id);
    } catch (error) {
      console.error('Erro ao gerar certificado:', error);
    } finally {
      setLoadingCertificado(false);
    }
  }

  const handleVerCertificado = () => {
    if (course.certificado?.pdf_path) {
      // Abrir o certificado existente
      window.open(`http://localhost:5000/uploads/${course.certificado.pdf_path}`, '_blank');
    } else if (onVerCertificado) {
      onVerCertificado(course.certificado);
    }
  }

  // Verificar se deve mostrar o botão de matrícula/desmatrícula
  const cursoConcluido = course.matriculado && course.progresso && course.progresso.porcentagem === 100;
  const temCertificado = course.certificado !== null && course.certificado !== undefined;

  return (
    <>
      <Card
        ref={cardRef}
        className="group relative overflow-hidden bg-gradient-to-br from-slate-900/95 via-slate-800/90 to-slate-900/95 border border-slate-700/40 hover:border-red-500/50 transition-all duration-500 cursor-pointer backdrop-blur-md h-full shadow-xl hover:shadow-2xl hover:shadow-red-500/10"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          boxShadow: isHovered 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(239, 68, 68, 0.1)' 
            : '0 10px 30px -10px rgba(0, 0, 0, 0.3)'
        }}
      >
        <CardContent className="p-0 h-full flex flex-col">
          {/* Thumbnail */}
          <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
            {course.capa ? (
              <img
                src={`http://localhost:5000/uploads/${course.capa}`}
                alt={course.titulo}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-115 group-hover:brightness-110"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-600/20 to-red-800/20">
                <Play className="w-12 h-12 text-red-400/50" />
              </div>
            )}

            {/* Overlay gradiente */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-500" />

            {/* Overlay com botões */}
            <div
              className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent flex items-center justify-center transition-all duration-500 ${isHovered ? "opacity-100" : "opacity-0"}`}
            >
              <div className="flex gap-3">
                <Button 
                  size="lg" 
                  className="bg-red-600/90 hover:bg-red-500 text-white border-0 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  onClick={() => onVerDetalhes && onVerDetalhes(course.id)}
                >
                  <Play className="w-5 h-5 mr-2" />
                  Assistir
                </Button>
                
                {/* Só mostrar botão de matrícula/desmatrícula se o curso não estiver concluído */}
                {!cursoConcluido && (
                  <Button
                    size="lg"
                    variant="outline"
                    className={`${course.matriculado 
                      ? 'bg-amber-600/20 hover:bg-amber-500/30 text-amber-300 border-amber-500/50' 
                      : 'bg-green-600/20 hover:bg-green-500/30 text-green-300 border-green-500/50'
                    } backdrop-blur-sm shadow-lg transition-all duration-300 hover:scale-105`}
                    onClick={handleMatriculaClick}
                    disabled={loadingMatricula}
                  >
                    {loadingMatricula ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      course.matriculado ? (
                        <>
                          <X className="w-5 h-5 mr-2" />
                          Desmatricular
                        </>
                      ) : (
                        <>
                          <Plus className="w-5 h-5 mr-2" />
                          Matricular
                        </>
                      )
                    )}
                  </Button>
                )}
              </div>
            </div>

            {/* Badge da categoria */}
            {course.categoria_nome && (
              <Badge className="absolute top-4 left-4 bg-gradient-to-r from-red-600 to-red-700 text-white border-0 text-xs font-semibold px-3 py-1 shadow-lg">
                {course.categoria_nome}
              </Badge>
            )}

            {/* Badge de status */}
            {course.matriculado && (
              <>
                {course.progresso && course.progresso.porcentagem === 100 ? (
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white border-0 text-xs font-semibold px-3 py-1 shadow-lg animate-pulse">
                    <Award className="w-3 h-3 mr-1" />
                    Concluído
                  </Badge>
                ) : course.progresso && course.progresso.porcentagem > 0 ? (
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0 text-xs font-semibold px-3 py-1 shadow-lg">
                    <Play className="w-3 h-3 mr-1" />
                    Em Progresso
                  </Badge>
                ) : (
                  <Badge className="absolute top-4 right-4 bg-gradient-to-r from-green-500 to-green-600 text-white border-0 text-xs font-semibold px-3 py-1 shadow-lg">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Matriculado
                  </Badge>
                )}
              </>
            )}

            {/* Progress bar (se houver progresso) */}
            {course.matriculado && course.progresso && course.progresso.porcentagem > 0 && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-3">
                <div className="bg-black/40 rounded-lg p-2 backdrop-blur-sm border border-white/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-white/90 font-semibold">Progresso</span>
                    <span className="text-xs text-white/90 font-bold">
                      {Math.round(course.progresso.porcentagem)}%
                    </span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-slate-700/50 rounded-full h-2.5 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-green-500 via-green-400 to-green-300 rounded-full transition-all duration-500 ease-out shadow-lg"
                        style={{ width: `${course.progresso.porcentagem}%` }}
                      >
                        <div className="h-full bg-gradient-to-r from-transparent to-white/20 rounded-full"></div>
                      </div>
                    </div>
                    {course.progresso.porcentagem === 100 && (
                      <div className="absolute -top-1 -right-1">
                        <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-white/80 mt-1 text-center font-medium">
                    {course.progresso.videos_concluidos || 0} de {course.progresso.total_videos || 0} vídeos assistidos
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Conteúdo do card */}
          <div className="p-6 flex-1 flex flex-col space-y-4">
            <div>
              <h3 className="font-bold text-white text-xl mb-2 line-clamp-2 group-hover:text-red-400 transition-colors duration-300 leading-tight">
                {course.titulo}
              </h3>

              {course.empresa_nome && (
                <p className="text-slate-400 text-sm font-medium">por <span className="text-red-400">{course.empresa_nome}</span></p>
              )}
            </div>

            <p className="text-slate-400 text-sm flex-1 line-clamp-3 leading-relaxed">
              {course.descricao || 'Descubra novos conhecimentos e habilidades com este curso abrangente.'}
            </p>

            {/* Estatísticas do curso */}
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-slate-400">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">{course.total_alunos || 0}</span>
                </div>
                
                {course.duracao && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{course.duracao}</span>
                  </div>
                )}
              </div>

              {/* Rating fictício para demonstração */}
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="text-slate-300 font-medium">4.8</span>
              </div>
            </div>

            {/* Botão de ação principal (visível sempre) */}
            <div className="pt-2">
              <Button 
                className={`w-full border-0 font-semibold py-3 transition-all duration-300 hover:scale-[1.02] shadow-lg hover:shadow-xl ${
                  cursoConcluido && temCertificado
                    ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white'
                    : course.matriculado && course.progresso && course.progresso.porcentagem === 100
                    ? 'bg-gradient-to-r from-yellow-600 to-yellow-700 hover:from-yellow-500 hover:to-yellow-600 text-white'
                    : course.matriculado && course.progresso && course.progresso.porcentagem > 0
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white'
                    : course.matriculado
                    ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white'
                    : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white'
                }`}
                onClick={
                  cursoConcluido && temCertificado 
                    ? handleVerCertificado 
                    : cursoConcluido 
                    ? handleGerarCertificado 
                    : () => onVerDetalhes && onVerDetalhes(course.id)
                }
                disabled={loadingCertificado}
              >
                {loadingCertificado ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Gerando Certificado...
                  </>
                ) : cursoConcluido && temCertificado ? (
                  <>
                    <Award className="w-4 h-4 mr-2" />
                    Ver Certificado
                  </>
                ) : course.matriculado && course.progresso && course.progresso.porcentagem === 100 ? (
                  <>
                    <Award className="w-4 h-4 mr-2" />
                    Gerar Certificado
                  </>
                ) : course.matriculado && course.progresso && course.progresso.porcentagem > 0 ? (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Continuar Assistindo
                  </>
                ) : course.matriculado ? (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar Curso
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Ver Detalhes
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de confirmação de desmatrícula */}
      <ModalDesmatricula
        isOpen={showModalDesmatricula}
        onClose={handleFecharModal}
        curso={course}
        onConfirmarDesmatricula={handleConfirmarDesmatricula}
        carregandoDesmatricula={loadingMatricula}
      />
    </>
  )
} 